import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "../../Components/Common/TableContainer";
import { getAgencies } from "common/services/agencies.service";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  buildListParams,
  parseFilter,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";
import Permission from "Components/Common/Permission";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
import BreadCrumb from "Components/Common/BreadCrumb";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  positionType: string;
  agency: string;
  dateRange: DateRange;
}

const defaultSort = [{ id: "createdAt", desc: true }];

const defaultFilterValue: FilterValue = {
  positionType: "",
  agency: "",
  dateRange: {
    fromDate: null,
    toDate: null,
  },
};

const ReportingAgencies = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: parsePage(searchParams),
    pageSize: parsePageSize(searchParams),
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() => parseSort(searchParams.get("sort"), defaultSort));

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));
  
  const [filterValue, setFilterValue] = useState(() => parseFilter(searchParams, defaultFilterValue));

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultFilterValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.positionType) {
        andConditions.push({
          positionType: {
            id: {
              $eq: filterValue.positionType,
            },
          },
        });
      }

      andConditions.push({
        $or: [
          {
            name: {
              $containsi: searchValue,
            },
          },
        ],
      });

      try {
        setLoading(true);

        const response: any = await getAgencies({
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: {
            payments: {
              count: true,
            },
          },
          filters: {
            $and: andConditions,
          },
        });
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams]
  );

  const columns = useMemo(
    () => [
      {
        header: "Reporting Agency",
        accessorKey: "name",
        id: "name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  // whiteSpace: "pre-wrap",
                  width: "400px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title border bg-primary-subtle text-danger rounded fs-24">
                      <i className="ri-earth-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    <Permission
                      alternative={
                        <Link
                          to={`/reporting-agencies/${rowData?.id}`}
                          className="text-body"
                        >
                          {cell.getValue()}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link
                        to={`/reporting-agencies/${rowData.id}`}
                        className="text-body"
                      >
                        {cell.getValue()}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.fullName || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },

      {
        header: "Number of Contracts",
        accessorKey: "payments.count",
        enableColumnFilter: false,
        enableSorting: false,
        id: "payments.count",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const count = rowData?.payments?.count || 0;
          if (count === 0) {
            return (
              <span className="badge bg-danger-subtle text-danger fs-11">
                No Contracts
              </span>
            );
          }
          if (count === 1) {
            return <>{count} Contract</>;
          }
          return <>{count} Contracts</>;
        },
      },

      // {
      //   header: "Status",
      //   accessorKey: "status",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: (cell: any) => {
      //     const status = isPaymentActive(
      //       cell.row.original.startDate || "",
      //       cell.row.original.endDate || ""
      //     );
      //     return (
      //       <React.Fragment>
      //         {status === null ? (
      //           <span className="badge bg-warning-subtle text-warning fs-11">
      //             N/A
      //           </span>
      //         ) : status ? (
      //           <span className="badge bg-success-subtle text-success fs-11">
      //             Active
      //           </span>
      //         ) : status === false ? (
      //           <span className="badge bg-danger-subtle text-danger fs-11">
      //             Not Active
      //           </span>
      //         ) : null}
      //       </React.Fragment>
      //     );
      //   },
      // },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="agency" action="findOne">
                  <DropdownItem
                    href={`/reporting-agencies/${rowData.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/reporting-agencies/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Reporting Agencies"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <Card>
            <CardBody>
              <Row className="g-3 ">
                <Col xxl={5} lg={6}>
                  {/* <h6 className="text-uppercase fs-12 mb-2">Search</h6> */}
                  <div className="search-box">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search reporting agencies..."
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        setPage(1);
                      }}
                      value={searchValue}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>
                <Col xxl={6} lg={6}>
                  {/* <h6 className="text-uppercase fs-12 mb-2">
                    Select Position type
                  </h6> */}
                  {/* <PositionTypeFilter
                    onChange={(value: any) => {
                      setFilterValue({ ...filterValue, positionType: value });
                      setPage(1);
                    }}
                    value={filterValue.positionType}
                  /> */}
                </Col>

                <Col xxl={1} lg={4}>
                  {/* <h6 className="text-uppercase fs-12 mb-xxl-4"> {` `}</h6> */}

                  <div className="w-100 d-flex gap-2">
                    <button
                      className="flex-grow-1 btn btn-light"
                      onClick={() => {
                        setFilterValue(defaultFilterValue);
                        setSearchValue("");
                        setPage(1);
                        setSorted([{ id: "createdAt", desc: true }]);
                      }}
                    >
                      <i className="ri-restart-line align-bottom me-1"></i>
                      Reset{" "}
                    </button>
                    {/*                     
                                  <Button
                                    color="primary"
                                    className="flex-grow-1"
                                    onClick={() => {
                                      setAdvancedFilter(true);
                                    }}
                                  >
                                    <i className="ri-equalizer-line align-bottom me-1"></i>
                                    Filters
                                  </Button> */}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={10}>
                  <h5 className="card-title mb-1">Reporting Agencies</h5>
                  <p className="mb-0 text-muted">
                    This page shows a list of reporting agencies and donors that
                    contribute information to the CIRT system.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2"></div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ReportingAgencies;
